import { appRoute, PRODUCT, WEB_ROUTES } from "./config";
import { pointerTilt } from "./motion";

export function renderMarketing(root: HTMLElement): void {
  document.title = `${PRODUCT.name} · Better talks, saved for later`;
  document.documentElement.dataset.surface = "marketing";
  const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
  if (skipLink) skipLink.href = "#marketing-main";
  setMetaDescription(
    "Convoful keeps private conversation prompts face down until people are together, then gives deliberately shared reads, listens, and watches a calm place to land.",
  );

  root.innerHTML = `
    <div class="marketing-shell">
      <header class="marketing-header">
        <a class="marketing-brand" href="${WEB_ROUTES.marketing}" aria-label="${PRODUCT.name} home">
          <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
          <span>${PRODUCT.name}</span>
        </a>
        <nav class="marketing-nav" aria-label="Marketing">
          <a href="#how-it-works">How it works</a>
          <a href="#audiences">Audiences</a>
          <a href="#privacy">Privacy</a>
          <a href="${WEB_ROUTES.howBuilt}">How I built this</a>
          <a class="marketing-nav-cta" href="${WEB_ROUTES.app}">Open private beta</a>
        </nav>
      </header>

      <main id="marketing-main" tabindex="-1">
        <section class="marketing-hero" aria-labelledby="marketing-title">
          <div class="marketing-hero-copy">
            <div class="marketing-hero-kicker">
              <p class="eyebrow">Private beta · made for people who matter</p>
              <span class="marketing-mobile-card-peek" aria-hidden="true">
                <span>✦</span>
              </span>
            </div>
            <h1 id="marketing-title">Better talks begin before anyone sits down.</h1>
            <p class="marketing-lede">
              Save the sparks you do not want to lose. When you are together,
              turn over one private Conversation Card at a time. When you are
              apart, share the right read, listen, or watch with the right
              person.
            </p>
            <div class="marketing-actions">
              <a class="marketing-primary-cta" href="${WEB_ROUTES.app}">
                Open private beta <span aria-hidden="true">→</span>
              </a>
              <a class="marketing-secondary-cta" href="${appRoute("?demo=1")}">
                Explore a synthetic preview
              </a>
            </div>
            <ul class="marketing-promises" aria-label="Convoful principles">
              <li><span aria-hidden="true">●</span> Face down by default</li>
              <li><span aria-hidden="true">●</span> Shared on purpose</li>
              <li><span aria-hidden="true">●</span> No separate Web identity</li>
            </ul>
          </div>

          <div class="marketing-ritual" aria-hidden="true">
            <div class="marketing-ritual-heading">
              <span>Tonight · Maya</span>
              <strong>12 private cards</strong>
            </div>
            <div class="marketing-deck">
              <div class="marketing-card marketing-card-back marketing-card-3">
                <span>✦</span>
              </div>
              <div class="marketing-card marketing-card-back marketing-card-2">
                <span>✦</span>
              </div>
              <div class="marketing-card marketing-card-front">
                <span class="marketing-card-label">A question for the room</span>
                <strong>What would make this week feel lighter?</strong>
                <span class="marketing-card-foot">Turn down when you are ready</span>
              </div>
            </div>
            <div class="marketing-ritual-foot">
              <span>One prompt visible</span>
              <span>11 still private</span>
            </div>
          </div>
        </section>

        <section class="marketing-story" id="how-it-works" aria-labelledby="story-title">
          <div class="marketing-story-heading">
            <div>
              <p class="eyebrow">Save now · return with intention</p>
              <h2 id="story-title">A small ritual for thoughts worth returning to.</h2>
            </div>
            <p>
              Keep the spark in the moment, protect it while it is private,
              then bring it back in the way the relationship needs.
            </p>
          </div>

          <div class="marketing-story-layout">
            <ol class="marketing-story-beats">
              <li class="marketing-story-beat" data-story-beat="save">
                <span class="marketing-beat-number" aria-hidden="true">01</span>
                <div>
                  <p class="eyebrow">Keep the spark</p>
                  <h3>Save it while it is still fresh.</h3>
                  <p>
                    Capture the question, link, or thought. Choose the person
                    or group it belongs with, without putting it on display.
                  </p>
                </div>
              </li>
              <li class="marketing-story-beat" data-story-beat="reveal">
                <span class="marketing-beat-number" aria-hidden="true">02</span>
                <div>
                  <p class="eyebrow">Reveal together</p>
                  <h3>Turn over one card when the room is ready.</h3>
                  <p>
                    Conversation Cards stay face down. The prompt appears only
                    when you choose to reveal that specific card together.
                  </p>
                </div>
              </li>
              <li class="marketing-story-beat" data-story-beat="library">
                <span class="marketing-beat-number" aria-hidden="true">03</span>
                <div>
                  <p class="eyebrow">Share on purpose</p>
                  <h3>Send the right read, listen, or watch.</h3>
                  <p>
                    When something is meant to be enjoyed apart, the Library
                    keeps it separate from your private Conversation Cards.
                  </p>
                  <small>
                    Shared Library membership is still expanding through the
                    private beta.
                  </small>
                </div>
              </li>
            </ol>

            <div class="marketing-story-stage-wrap">
              <div
                class="marketing-story-stage"
                data-active-beat="save"
                data-motion="enhanced"
                aria-hidden="true"
              >
                <div class="marketing-stage-orbit marketing-stage-orbit-one"></div>
                <div class="marketing-stage-orbit marketing-stage-orbit-two"></div>
                <div class="marketing-stage-card marketing-stage-card-back">
                  <span class="marketing-stage-spark">✦</span>
                  <span class="marketing-stage-private">Face down · Maya</span>
                </div>
                <div class="marketing-stage-card marketing-stage-card-front">
                  <span>A question for the room</span>
                  <strong>What should we make more room for this month?</strong>
                  <small>Revealed together</small>
                </div>
                <div class="marketing-stage-library">
                  <span class="marketing-stage-format">Listen</span>
                  <strong>A little rabbit hole for later</strong>
                  <span>Shared deliberately with Maya</span>
                  <i aria-hidden="true">↗</i>
                </div>
                <div class="marketing-stage-caption">
                  <span data-stage-caption="save">Private until you choose otherwise</span>
                  <span data-stage-caption="reveal">One prompt, one shared moment</span>
                  <span data-stage-caption="library">A separate place for asynchronous sharing</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="marketing-rhythms" id="two-rhythms" aria-labelledby="rhythms-title">
          <div class="marketing-section-heading">
            <p class="eyebrow">One space · two rhythms</p>
            <h2 id="rhythms-title">Together at the table. Thoughtful from afar.</h2>
          </div>
          <div class="marketing-mode-grid">
            <article class="marketing-mode-card marketing-mode-conversation">
              <span class="marketing-mode-number">01</span>
              <p class="eyebrow">Conversation Cards</p>
              <h3>Make room for one good question.</h3>
              <p>
                Choose who is here, keep every prompt face down, and reveal only
                the card the room is ready to discuss.
              </p>
              <ul>
                <li>Phone-first private Stack</li>
                <li>Table-scale desktop ritual</li>
                <li>Keyboard and pointer ready</li>
              </ul>
            </article>
            <article class="marketing-mode-card marketing-mode-library">
              <span class="marketing-mode-number">02</span>
              <p class="eyebrow">Library</p>
              <h3>Give shared things a calm place to land.</h3>
              <p>
                Keep reads, listens, and watches with an explicit audience,
                while your private Notes and Summary remain yours.
              </p>
              <ul>
                <li>One publication per audience</li>
                <li>Shared context stays explicit</li>
                <li>Private search stays private</li>
              </ul>
            </article>
          </div>
        </section>

        <section class="marketing-audiences" id="audiences" aria-labelledby="audiences-title">
          <div class="marketing-audiences-heading">
            <p class="eyebrow">People, not folders</p>
            <h2 id="audiences-title">For the relationships you actually have.</h2>
            <p>
              One person, a family, a group of friends—or just you. Each
              audience keeps its own context, so the thought returns with the
              people it was meant for.
            </p>
          </div>
          <div class="marketing-audience-grid">
            <article class="marketing-audience-card marketing-audience-partner">
              <span aria-hidden="true">M</span>
              <p class="eyebrow">A partner</p>
              <h3>Save the question for the next unhurried moment.</h3>
            </article>
            <article class="marketing-audience-card marketing-audience-friends">
              <span aria-hidden="true">F</span>
              <p class="eyebrow">Friends</p>
              <h3>Keep the rabbit hole until you are together.</h3>
            </article>
            <article class="marketing-audience-card marketing-audience-family">
              <span aria-hidden="true">↟</span>
              <p class="eyebrow">Family</p>
              <h3>Hold onto the story you still want to hear.</h3>
            </article>
            <article class="marketing-audience-card marketing-audience-group">
              <span aria-hidden="true">•••</span>
              <p class="eyebrow">A group</p>
              <h3>Give the room one face-down ritual, not one exposed list.</h3>
            </article>
            <article class="marketing-audience-card marketing-audience-me">
              <span aria-hidden="true">Me</span>
              <p class="eyebrow">Me</p>
              <h3>Keep something for your own later without learning a separate mode.</h3>
            </article>
          </div>
        </section>

        <section class="marketing-privacy" id="privacy" aria-labelledby="privacy-title">
          <div>
            <p class="eyebrow">Privacy is part of the interaction</p>
            <h2 id="privacy-title">Not everything saved is meant to be shared.</h2>
          </div>
          <div class="marketing-privacy-copy">
            <p>
              Conversation prompts stay concealed until the selected audience
              is physically together and chooses to reveal one.
            </p>
            <p>
              Library items are shared through explicit audience publications.
              Owner Notes, private Summary, search terms, and Conversation
              destinations do not become recipient copy.
            </p>
            <p>
              One Convoful account is being built for iPhone and Web, with
              controls to sign out this browser or every session. Current
              access remains a small, provisioned private beta.
            </p>
          </div>
        </section>

        <section class="marketing-availability" id="availability" aria-labelledby="availability-title">
          <div class="marketing-availability-heading">
            <p class="eyebrow">Private beta now · public launch later</p>
            <h2 id="availability-title">A real product in active playtesting.</h2>
            <p>
              Existing participants can open their private Web space. Public
              App Store and Google Play availability, self-service signup, and
              pricing have not been announced.
            </p>
            <div class="marketing-actions">
              <a class="marketing-primary-cta" href="${WEB_ROUTES.app}">
                Already invited? Open your space <span aria-hidden="true">→</span>
              </a>
              <a class="marketing-secondary-cta" href="${appRoute("?demo=1")}">
                Explore the synthetic preview
              </a>
            </div>
          </div>
          <div class="marketing-truth-grid">
            <article class="marketing-truth-card marketing-truth-live">
              <span aria-hidden="true">✓</span>
              <p class="eyebrow">Available in the private beta</p>
              <h3>Save, organise, and reveal with intention.</h3>
              <ul>
                <li>Audience-specific private Conversation Cards</li>
                <li>Face-down Stack and table-scale Web rituals</li>
                <li>iPhone share-sheet capture</li>
                <li>Review and organise your saved bookmarks on the Web</li>
              </ul>
            </article>
            <article class="marketing-truth-card marketing-truth-roadmap">
              <span aria-hidden="true">↗</span>
              <p class="eyebrow">Still being built and tested</p>
              <h3>Growing carefully, without pretending it is done.</h3>
              <ul>
                <li>New-member invitations and broader Library sharing</li>
                <li>Automatic enrichment and semantic search everywhere</li>
                <li>Self-service signup and public store releases</li>
                <li>Pricing and shared paid entitlements</li>
              </ul>
            </article>
          </div>
        </section>

        <section class="marketing-faq" id="faq" aria-labelledby="faq-title">
          <div class="marketing-faq-heading">
            <p class="eyebrow">The useful questions</p>
            <h2 id="faq-title">A little more, before you open a card.</h2>
          </div>
          <div class="marketing-faq-list">
            <details>
              <summary>What is a Conversation Card?</summary>
              <p>
                A private prompt you save for a person or group. It is not
                shown to them in advance; you reveal it together on one device
                when the moment is right.
              </p>
            </details>
            <details>
              <summary>What is the Library?</summary>
              <p>
                A separate place for material intended to be enjoyed
                asynchronously. Library sharing and new-member access are
                still being expanded in the private beta.
              </p>
            </details>
            <details>
              <summary>Is everything I save shared?</summary>
              <p>
                No. Conversation Cards remain private before reveal. Owner
                Notes, private Summary and search terms, and device-local
                card-back photos do not become recipient copy. Only a
                deliberate Library publication and its explicit Shared Notes
                may cross that boundary.
              </p>
            </details>
            <details>
              <summary>Who is Convoful for?</summary>
              <p>
                Partners, friends, families, groups, and people saving things
                for themselves. “Me” is an ordinary audience, not a separate
                product.
              </p>
            </details>
            <details>
              <summary>Where can I use it?</summary>
              <p>
                Convoful is currently being tested through private iPhone,
                Android, and Web experiences. It is not yet publicly available
                in the App Store or Google Play.
              </p>
            </details>
            <details>
              <summary>How much does it cost?</summary>
              <p>
                Pricing has not been announced. The current private beta is
                not a public paid signup.
              </p>
            </details>
            <details>
              <summary>Can I create an account?</summary>
              <p>
                Self-service signup is not open yet. Existing participants can
                open their space; everyone else can explore the synthetic
                preview.
              </p>
            </details>
          </div>
        </section>

        <section class="marketing-support" aria-labelledby="support-title">
          <span class="marketing-support-mark" aria-hidden="true">?</span>
          <div>
            <p class="eyebrow">Support during the private beta</p>
            <h2 id="support-title">Need help opening your space?</h2>
            <p>Use the support channel provided with your invitation.</p>
          </div>
        </section>
      </main>

      <footer class="marketing-footer">
        <div>
          <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
          <span><strong>${PRODUCT.name}</strong> · Private beta</span>
        </div>
        <div class="marketing-footer-links">
          <a href="#faq">FAQ</a>
          <a href="${WEB_ROUTES.howBuilt}">How I built this</a>
          <a href="${WEB_ROUTES.app}">Open your space <span aria-hidden="true">→</span></a>
        </div>
      </footer>
    </div>
  `;

  setSocialMeta(
    "Convoful · Better talks, saved for later",
    "Save private prompts for the people who matter, reveal them together, and share reads, listens, and watches on purpose.",
  );
  setStructuredData();
  installMarketingHero(root);
  installMarketingStory(root);
}

function setMetaDescription(content: string): void {
  let description = document.querySelector<HTMLMetaElement>(
    'meta[name="description"]',
  );
  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.append(description);
  }
  description.content = content;
}

function setSocialMeta(title: string, description: string): void {
  const values = new Map([
    ["og:type", "website"],
    ["og:title", title],
    ["og:description", description],
    ["og:url", "https://convoful.com/"],
  ]);
  for (const [property, content] of values) {
    let meta = document.querySelector<HTMLMetaElement>(
      `meta[property="${property}"]`,
    );
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", property);
      document.head.append(meta);
    }
    meta.content = content;
  }
}

function setStructuredData(): void {
  const id = "convoful-marketing-structured-data";
  document.getElementById(id)?.remove();
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: PRODUCT.name,
    url: "https://convoful.com/",
    description:
      "Private Conversation Cards revealed together, with a separate Library for deliberate sharing.",
  });
  document.head.append(script);
}

function installMarketingHero(root: HTMLElement): void {
  const hero = root.querySelector<HTMLElement>(".marketing-hero");
  const ritual = root.querySelector<HTMLElement>(".marketing-ritual");
  if (!hero || !ritual) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );
  const finePointer = window.matchMedia(
    "(hover: hover) and (pointer: fine)",
  );
  if (reducedMotion.matches || !finePointer.matches) {
    ritual.dataset.motion = "static";
    return;
  }
  ritual.dataset.motion = "enhanced";

  const setMotion = (
    tiltX: number,
    tiltY: number,
  ): void => {
    const values = new Map([
      ["--hero-tilt-x", `${(tiltY * 0.7).toFixed(3)}deg`],
      ["--hero-tilt-y", `${(-tiltX * 0.7).toFixed(3)}deg`],
      ["--hero-front-x", `${(tiltY * 2.1).toFixed(3)}px`],
      ["--hero-front-y", `${(-tiltX * 1.2).toFixed(3)}px`],
      ["--hero-card-two-x", `${(tiltY * 3.2).toFixed(3)}px`],
      ["--hero-card-two-y", `${(-tiltX * 1.7).toFixed(3)}px`],
      ["--hero-card-two-turn", `${(tiltY * 0.45).toFixed(3)}deg`],
      ["--hero-card-three-x", `${(-tiltY * 3.2).toFixed(3)}px`],
      ["--hero-card-three-y", `${(tiltX * 1.7).toFixed(3)}px`],
      ["--hero-card-three-turn", `${(-tiltY * 0.45).toFixed(3)}deg`],
    ]);
    for (const [property, value] of values) {
      ritual.style.setProperty(property, value);
    }
  };
  const reset = (): void => setMotion(0, 0);
  reset();

  hero.addEventListener("pointermove", (event) => {
    if (event.pointerType && event.pointerType !== "mouse") return;
    const tilt = pointerTilt(
      event.clientX,
      event.clientY,
      hero.getBoundingClientRect(),
      1,
    );
    setMotion(tilt.x, tilt.y);
  });
  hero.addEventListener("pointerleave", reset);
  hero.addEventListener("pointercancel", reset);
}

function installMarketingStory(root: HTMLElement): void {
  const story = root.querySelector<HTMLElement>(".marketing-story");
  const stage = story?.querySelector<HTMLElement>(".marketing-story-stage");
  const beats = Array.from(
    story?.querySelectorAll<HTMLElement>("[data-story-beat]") ?? [],
  );
  if (!story || !stage || beats.length === 0) return;

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );
  if (reducedMotion.matches) {
    stage.dataset.motion = "static";
    return;
  }

  const validBeats = new Set(["save", "reveal", "library"]);
  const activate = (beat: string | undefined): void => {
    if (!beat || !validBeats.has(beat)) return;
    stage.dataset.activeBeat = beat;
    for (const item of beats) {
      item.toggleAttribute("data-is-active", item.dataset.storyBeat === beat);
    }
  };
  activate("save");

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible?.target instanceof HTMLElement) {
        activate(visible.target.dataset.storyBeat);
      }
    }, {
      rootMargin: "-30% 0px -42%",
      threshold: [0.15, 0.45, 0.75],
    });
    beats.forEach((beat) => observer.observe(beat));
  }

  const resetTilt = (): void => {
    stage.style.setProperty("--story-tilt-x", "0.000");
    stage.style.setProperty("--story-tilt-y", "0.000");
  };

  stage.addEventListener("pointermove", (event) => {
    if (event.pointerType && event.pointerType !== "mouse") return;
    const tilt = pointerTilt(
      event.clientX,
      event.clientY,
      stage.getBoundingClientRect(),
      1,
    );
    stage.style.setProperty("--story-tilt-x", tilt.y.toFixed(3));
    stage.style.setProperty("--story-tilt-y", (-tilt.x).toFixed(3));
  });
  stage.addEventListener("pointerleave", resetTilt);
  stage.addEventListener("pointercancel", resetTilt);
}
